import {
  InvestorType,
  DealVolume,
  TeamSize,
} from '../components/Trial/PlanQuestionnaire'

export interface RecommendationResult {
  plan: 'Core' | 'Scale'
  confidence: number
  reasoning: string[]
  relevantFeatures: string[]
}

interface QuestionnaireAnswers {
  dealVolume: DealVolume
  investorType: InvestorType
  teamSize: TeamSize
}

export function recommendPlan(
  answers: QuestionnaireAnswers,
): RecommendationResult {
  const scores = {
    Core: 0,
    Scale: 0,
  }

  // Score based on deal volume (highest weight)
  const dealVolumeNum = parseDealVolume(answers.dealVolume)
  if (dealVolumeNum <= 2) {
    scores.Core += 4
  } else if (dealVolumeNum <= 8) {
    scores.Core += 2
    scores.Scale += 2
  } else if (dealVolumeNum <= 20) {
    scores.Scale += 4
  } else {
    scores.Scale += 5
  }

  // Score based on investor type
  switch (answers.investorType) {
    case 'new':
      scores.Core += 3
      break
    case 'buy-hold':
      scores.Core += 2
      scores.Scale += 1
      break
    case 'fix-flip':
      scores.Core += 1
      scores.Scale += 2
      break
    case 'wholesaler':
      scores.Scale += 3
      break
    case 'str':
      scores.Core += 1
      scores.Scale += 2
      break
  }

  // Score based on team size
  const teamSizeNum = parseTeamSize(answers.teamSize)
  if (teamSizeNum === 1) {
    scores.Core += 2
  } else if (teamSizeNum <= 5) {
    scores.Core += 1
    scores.Scale += 1
  } else if (teamSizeNum <= 15) {
    scores.Scale += 2
  } else {
    scores.Scale += 3
  }

  // Determine recommended plan
  const recommendedPlan = scores.Scale > scores.Core ? 'Scale' : 'Core'
  const totalScore = scores.Core + scores.Scale
  const confidence = Math.round(
    (Math.max(scores.Core, scores.Scale) / totalScore) * 100,
  )

  // Generate reasoning
  const reasoning = generateReasoning(answers, recommendedPlan)

  // Get relevant features
  const relevantFeatures = getRelevantFeatures(
    answers.investorType,
    recommendedPlan,
  )

  return {
    plan: recommendedPlan,
    confidence,
    reasoning,
    relevantFeatures,
  }
}

function parseDealVolume(volume: DealVolume): number {
  const match = volume.match(/\d+/)
  return match ? parseInt(match[0]) : 0
}

function parseTeamSize(size: TeamSize): number {
  if (size === '1') return 1
  if (size === '16+') return 16
  const match = size.match(/\d+/)
  return match ? parseInt(match[0]) : 1
}

function generateReasoning(
  answers: QuestionnaireAnswers,
  plan: 'Core' | 'Scale',
): string[] {
  const reasons: string[] = []

  const dealVolumeNum = parseDealVolume(answers.dealVolume)

  // Deal volume reasoning
  if (plan === 'Core') {
    if (dealVolumeNum <= 2) {
      reasons.push(
        `Perfect for investors closing ${answers.dealVolume} deals per month`,
      )
    } else {
      reasons.push(`Great starting point with room to scale as you grow`)
    }
  } else {
    if (dealVolumeNum >= 9) {
      reasons.push(
        `Unlimited offers support your ${answers.dealVolume} deals/month volume`,
      )
    } else {
      reasons.push(`Room to grow with unlimited markets and offers`)
    }
  }

  // Investor type reasoning
  const investorTypeReasons: Record<
    InvestorType,
    { Core: string; Scale: string }
  > = {
    wholesaler: {
      Core: 'Includes bulk offer automation for wholesaling',
      Scale: 'Unlimited offers perfect for high-volume wholesaling',
    },
    'fix-flip': {
      Core: 'AI analysis helps evaluate renovation opportunities',
      Scale: 'Custom AI training for your specific flip criteria',
    },
    'buy-hold': {
      Core: 'Cash flow analysis for rental property evaluation',
      Scale: 'Multi-market tracking for portfolio expansion',
    },
    str: {
      Core: 'Property analysis tools for STR evaluation',
      Scale: 'Multi-market campaigns for STR portfolio growth',
    },
    new: {
      Core: 'Simple, affordable plan to learn and grow',
      Scale: 'All features to accelerate your learning curve',
    },
  }

  reasons.push(investorTypeReasons[answers.investorType][plan])

  // Team size reasoning
  const teamSizeNum = parseTeamSize(answers.teamSize)
  if (teamSizeNum === 1) {
    reasons.push('Designed for solo investors with focused features')
  } else if (teamSizeNum <= 5) {
    reasons.push(
      plan === 'Core'
        ? 'Supports small team collaboration'
        : 'Advanced features for team coordination',
    )
  } else {
    reasons.push('Enterprise-grade features for larger teams')
  }

  return reasons
}

function getRelevantFeatures(
  investorType: InvestorType,
  plan: 'Core' | 'Scale',
): string[] {
  const featuresByType: Record<
    InvestorType,
    { Core: string[]; Scale: string[] }
  > = {
    wholesaler: {
      Core: [
        '3,000 automated offers/month',
        'Response tracking & management',
        'Deal pipeline analytics',
      ],
      Scale: [
        'Unlimited automated offers',
        'Multi-market campaigns',
        'White-labeled offers',
      ],
    },
    'fix-flip': {
      Core: [
        'AI-powered property analysis',
        'Deal pipeline analytics',
        'Custom offer templates',
      ],
      Scale: [
        'Custom AI training',
        'Advanced analytics',
        'API access for integrations',
      ],
    },
    'buy-hold': {
      Core: [
        'AI-powered property analysis',
        'Response tracking',
        'Deal pipeline analytics',
      ],
      Scale: [
        'Unlimited markets',
        'Custom AI training',
        'Multi-market campaigns',
      ],
    },
    str: {
      Core: [
        'AI-powered property analysis',
        'Multi-market campaigns',
        'Custom offer templates',
      ],
      Scale: [
        'Unlimited markets',
        'Custom AI training',
        'White-labeled offers',
      ],
    },
    new: {
      Core: [
        'AI-powered property analysis',
        'Email & phone support',
        'Deal pipeline analytics',
      ],
      Scale: [
        'Dedicated account manager',
        'Priority support (24/7)',
        'Custom AI training',
      ],
    },
  }

  return featuresByType[investorType][plan]
}
